import { type CSSProperties, type HTMLAttributes, type ReactElement, type Ref } from 'react';
import clsx from 'clsx';

export interface IconProps extends HTMLAttributes<HTMLSpanElement> {
  /** Pass an SVG element; Icon just wraps it with sizing + color. */
  children: ReactElement;
  /** Square size in px (applies to width + height of the wrapper). Default 20. */
  size?: number;
  /** CSS color keyword, hex, or a var(). Default = `currentColor`. */
  color?: string;
  /** Screen-reader label. Omit to mark as decorative. */
  label?: string;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * Icon — thin wrapper around an inline SVG. Provides consistent sizing,
 * color via `currentColor`, and accessibility labeling. When a `label` is
 * passed, it becomes an `img` role; without it, the icon is marked
 * `aria-hidden` so screen readers ignore decorative usage.
 */
export function Icon({
  children,
  size = 20,
  color,
  label,
  className,
  style,
  ref,
  ...rest
}: IconProps) {
  const wrapperStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    color: color ?? 'currentColor',
    lineHeight: 0,
    flexShrink: 0,
    ...style,
  };
  const a11y = label
    ? { role: 'img' as const, 'aria-label': label }
    : { 'aria-hidden': 'true' as const };

  return (
    <span ref={ref} className={clsx('fluent-icon', className)} style={wrapperStyle} {...a11y} {...rest}>
      {children}
    </span>
  );
}
